import React, { useState, useEffect } from 'react';
import { X, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Success Animation Component
const SuccessAnimation = ({ onClose, packageName }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="absolute inset-0 z-20 flex items-center justify-center bg-slate-900/98 backdrop-blur-sm rounded-2xl overflow-hidden">
      {/* Animated particles */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 rounded-full animate-float-particle"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              backgroundColor: i % 2 === 0 ? '#ec4899' : '#3b82f6',
              animationDelay: `${Math.random() * 2}s`,
              animationDuration: `${2 + Math.random() * 2}s`,
            }}
          />
        ))}
      </div>
      
      {/* Success content */}
      <div className="relative text-center px-8">
        {/* Animated checkmark circle */}
        <div className="relative mx-auto mb-6">
          {/* Outer glow ring */}
          <div className="absolute inset-0 w-24 h-24 mx-auto bg-gradient-to-r from-pink-500 to-blue-500 rounded-full animate-ping opacity-20"></div>
          
          {/* Main circle */}
          <div className="relative w-24 h-24 mx-auto bg-gradient-to-br from-pink-500 to-blue-600 rounded-full flex items-center justify-center animate-success-bounce shadow-2xl shadow-pink-500/50">
            <CheckCircle className="w-12 h-12 text-white animate-success-check" strokeWidth={2.5} />
          </div>
        </div>
        
        {/* Text */}
        <h3 className="text-2xl font-bold text-white mb-2 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
          Inquiry Sent!
        </h3>
        <p className="text-gray-400 animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
          We'll contact you within 24 hours about <span className="text-pink-400">{packageName}</span>
        </p>
        
        {/* Progress bar */}
        <div className="mt-6 w-48 h-1 bg-slate-700 rounded-full mx-auto overflow-hidden">
          <div className="h-full bg-gradient-to-r from-pink-500 to-blue-500 rounded-full animate-progress-bar"></div>
        </div>
      </div>
    </div>
  );
};

export const PackageInquiryModal = ({ package: pkg, isOpen, onClose }) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API}/package-inquiry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          package_id: pkg.id,
          package_name: pkg.name,
          package_price: pkg.price,
          ...formData
        }),
      });

      if (response.ok) {
        setShowSuccess(true);
        setFormData({ customer_name: '', customer_email: '', customer_phone: '', message: '' });
      } else {
        throw new Error('Failed to submit inquiry');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to submit inquiry. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccess(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl bg-slate-900 border border-pink-500/30 rounded-2xl shadow-2xl animate-fade-in-up">
        {/* Success Animation Overlay */}
        {showSuccess && (
          <SuccessAnimation 
            onClose={handleSuccessClose} 
            packageName={pkg.name}
          />
        )}
        
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors duration-300 z-10"
        >
          <X size={24} />
        </button>

        {/* Header */}
        <div className="p-8 border-b border-slate-800">
          <h2 className="text-3xl font-bold text-white mb-2">Select {pkg.name}</h2>
          <div className="flex items-baseline gap-2">
            <span className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-pink-400">
              {pkg.price} €
            </span>
            <span className="text-gray-400">+ TVA</span>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          <div>
            <label htmlFor="customer_name" className="block text-sm font-medium text-gray-300 mb-2">
              Name *
            </label>
            <Input
              id="customer_name"
              name="customer_name"
              type="text"
              required
              value={formData.customer_name}
              onChange={handleChange}
              className="bg-slate-800 border-slate-700 text-white placeholder:text-gray-500 focus:border-pink-500 focus:ring-pink-500"
              placeholder="Your name"
            />
          </div>

          <div>
            <label htmlFor="customer_email" className="block text-sm font-medium text-gray-300 mb-2">
              Email *
            </label>
            <Input
              id="customer_email"
              name="customer_email"
              type="email"
              required
              value={formData.customer_email}
              onChange={handleChange}
              className="bg-slate-800 border-slate-700 text-white placeholder:text-gray-500 focus:border-pink-500 focus:ring-pink-500"
              placeholder="your@email.com"
            />
          </div>

          <div>
            <label htmlFor="customer_phone" className="block text-sm font-medium text-gray-300 mb-2">
              Phone
            </label>
            <Input
              id="customer_phone"
              name="customer_phone"
              type="tel"
              value={formData.customer_phone}
              onChange={handleChange}
              className="bg-slate-800 border-slate-700 text-white placeholder:text-gray-500 focus:border-pink-500 focus:ring-pink-500"
              placeholder="+1 (234) 567-890"
            />
          </div>

          <div>
            <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
              Additional Information
            </label>
            <Textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={4}
              className="bg-slate-800 border-slate-700 text-white placeholder:text-gray-500 focus:border-pink-500 focus:ring-pink-500 resize-none"
              placeholder="Tell us about your requirements..."
            />
          </div>

          <div className="flex gap-4">
            <Button
              type="button"
              onClick={onClose}
              className="flex-1 bg-slate-800 hover:bg-slate-700 text-white py-6 text-lg"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-gradient-to-r from-blue-600 to-pink-600 hover:from-blue-700 hover:to-pink-700 text-white py-6 text-lg font-semibold transition-all duration-300 disabled:opacity-50"
            >
              {isSubmitting ? 'Sending...' : 'Submit Inquiry'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
